import { useInfiniteQuery } from '@tanstack/react-query';
import type {
  ConversationMessageT,
  FetchChatResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { fetchPrimaryChatByUserId } from '@/shared/api/lotus';

import { userChatKeys } from './keys';

/**
 * Backend cap (`CONVERSATION_MESSAGE_PAGE_SIZE`). When a page returns this many
 * messages, there are likely older messages still to fetch.
 */
export const PRIMARY_CHAT_PAGE_SIZE = 50;

export type FetchPrimaryChatPage = typeof fetchPrimaryChatByUserId;

/**
 * Cursor-paginated query definition for the primary chat. Each page is up to
 * `PRIMARY_CHAT_PAGE_SIZE` messages, ordered oldest-first within the page.
 * Subsequent pages walk backwards in time using the oldest message id from the
 * previous page as `before`.
 *
 * The `'infinite'` suffix on the queryKey keeps this isolated from any future
 * single-page `useQuery` against the same user, while still being matched by
 * prefix-based `invalidateQueries({ queryKey: userChatKeys.primaryByUser(...) })`
 * calls (e.g. in `CareStateQuerySync`).
 */
export function primaryChatInfiniteQueryDefinition(
  userId: string,
  fetchPage: FetchPrimaryChatPage = fetchPrimaryChatByUserId
) {
  return {
    queryKey: [...userChatKeys.primaryByUser(userId), 'infinite'] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }: { pageParam?: string; signal?: AbortSignal }) =>
      fetchPage(userId, pageParam ? { before: pageParam } : undefined, signal),
    getNextPageParam: (lastPage: FetchChatResponseT) => {
      // Backend returns at most PRIMARY_CHAT_PAGE_SIZE messages oldest-first
      // within the page. If we got a full page, there are likely more older
      // messages; cursor backwards from the oldest id we have.
      if (lastPage.messages.length < PRIMARY_CHAT_PAGE_SIZE) {
        return undefined;
      }
      return lastPage.messages[0]?.id;
    },
    meta: {
      toast: {
        title: 'Could not load chat',
        description: 'Primary conversation could not be fetched.',
      },
    },
  } as const;
}

/**
 * How often an open Chat History refetches, to show messages that arrive while
 * it is on screen.
 *
 * A new message, such as the patient's reply (including the sandbox LLM
 * patient's) or a Lotus follow-up, changes nothing else the workspace listens
 * to: `CareStateQuerySync` refetches the chat only when care state moves. So
 * without polling the chat stays as it was when it mounted.
 *
 * This may become an issue at scale: every clinician with a Chat History open
 * sends one request per interval, and each refetch reloads every page loaded
 * so far. If that matters, push the change instead: have the care-state stream
 * the workspace already holds (`/care/{userId}/state/events/streaming`) send
 * an event when the patient's primary conversation gains a message, and
 * refetch on that event rather than on a timer.
 */
export const PRIMARY_CHAT_REFETCH_INTERVAL_MS = 10_000;

/**
 * Fetch the full primary chat for a user via React Query infinite pagination,
 * and keep it current while it is on screen: it refetches every
 * `PRIMARY_CHAT_REFETCH_INTERVAL_MS` while the tab is visible, and on every
 * mount (`staleTime: 0`), so switching back to Chat History always shows the
 * latest messages. Only the Chat History tab uses it, and the dock mounts only
 * its active tab, so nothing polls while the chat is not shown.
 */
export function usePrimaryChatInfiniteQuery(
  userId: string | undefined,
  fetchPage: FetchPrimaryChatPage = fetchPrimaryChatByUserId
) {
  return useInfiniteQuery({
    ...primaryChatInfiniteQueryDefinition(userId ?? '', fetchPage),
    enabled: !!userId,
    staleTime: 0,
    refetchInterval: PRIMARY_CHAT_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export interface FlattenedPrimaryChat {
  /**
   * All loaded messages, ordered oldest-first.
   */
  messages: ConversationMessageT[];
  /**
   * Conversation id for the loaded pages (taken from page 0). `null` if no
   * pages have loaded yet.
   */
  conversationId: string | null;
}

/**
 * Flatten the page array returned by `usePrimaryChatInfiniteQuery` into a
 * single oldest-first list of messages plus a stable `conversationId`.
 *
 * Pages come back in fetch order (newest page first, then progressively older
 * pages), and each page is internally oldest-first. To get a single oldest->
 * newest list, we walk pages in reverse and concatenate their messages.
 */
export function flattenPrimaryChatPages(
  pages: FetchChatResponseT[] | undefined
): FlattenedPrimaryChat {
  if (!pages || pages.length === 0) {
    return { messages: [], conversationId: null };
  }

  const messages: ConversationMessageT[] = [];
  for (let i = pages.length - 1; i >= 0; i--) {
    const page = pages[i];
    if (!page) {
      continue;
    }
    for (const message of page.messages) {
      messages.push(message);
    }
  }

  return {
    messages,
    conversationId: pages[0]?.conversationId ?? null,
  };
}
